import React, { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const CUSTOMER_SEARCH_DEBOUNCE_MS = 200;

export default function CustomerMergeModal({ showModal, onClose, onSuccess }) {
  // Source (left column) state
  const [sourceSearchQuery, setSourceSearchQuery] = useState('');
  const [sourceSearchResults, setSourceSearchResults] = useState([]);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [isSourceSearching, setIsSourceSearching] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceStats, setSourceStats] = useState(null);
  const [loadingSourceStats, setLoadingSourceStats] = useState(false);

  // Destination (right column) state
  const [destSearchQuery, setDestSearchQuery] = useState('');
  const [destSearchResults, setDestSearchResults] = useState([]);
  const [showDestDropdown, setShowDestDropdown] = useState(false);
  const [isDestSearching, setIsDestSearching] = useState(false);
  const [selectedDest, setSelectedDest] = useState(null);
  const [destStats, setDestStats] = useState(null);
  const [loadingDestStats, setLoadingDestStats] = useState(false);

  // Merge state
  const [isMerging, setIsMerging] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteSource, setDeleteSource] = useState(true);
  const [mergeRelationships, setMergeRelationships] = useState(true);

  // Refs for debouncing
  const sourceSearchTimeoutRef = useRef(null);
  const destSearchTimeoutRef = useRef(null);
  const sourceDropdownRef = useRef(null);
  const destDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(e.target)) {
        setShowSourceDropdown(false);
      }
      if (destDropdownRef.current && !destDropdownRef.current.contains(e.target)) {
        setShowDestDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Reset modal state when opened/closed
  useEffect(() => {
    if (showModal) {
      setSourceSearchQuery('');
      setSourceSearchResults([]);
      setShowSourceDropdown(false);
      setSelectedSource(null);
      setSourceStats(null);
      setDestSearchQuery('');
      setDestSearchResults([]);
      setShowDestDropdown(false);
      setSelectedDest(null);
      setDestStats(null);
      setError('');
      setSuccess('');
      setDeleteSource(true);
    }
  }, [showModal]);

  // Fetch customer stats when a customer is selected
  const fetchStats = useCallback(async (customerId) => {
    try {
      const res = await fetch(`${API_URL}/customers/${customerId}/stats`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        return data.stats;
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
    return null;
  }, []);

  // Search customers for autocomplete
  const searchCustomers = useCallback(async (query, isSource) => {
    if (!query || query.trim() === '') {
      if (isSource) {
        setSourceSearchResults([]);
        setShowSourceDropdown(false);
      } else {
        setDestSearchResults([]);
        setShowDestDropdown(false);
      }
      return;
    }

    if (isSource) setIsSourceSearching(true);
    else setIsDestSearching(true);

    try {
      const res = await fetch(`${API_URL}/customers/search/autocomplete?q=${encodeURIComponent(query)}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (isSource) {
          setSourceSearchResults(data.customers);
          setShowSourceDropdown(true);
        } else {
          setDestSearchResults(data.customers);
          setShowDestDropdown(true);
        }
      }
    } catch (err) {
      console.error('Failed to search customers:', err);
    } finally {
      if (isSource) setIsSourceSearching(false);
      else setIsDestSearching(false);
    }
  }, []);

  // Debounced search handlers
  const handleSourceSearchChange = (e) => {
    const query = e.target.value;
    setSourceSearchQuery(query);
    if (sourceSearchTimeoutRef.current) clearTimeout(sourceSearchTimeoutRef.current);
    sourceSearchTimeoutRef.current = setTimeout(() => {
      searchCustomers(query, true);
    }, CUSTOMER_SEARCH_DEBOUNCE_MS);
  };

  const handleDestSearchChange = (e) => {
    const query = e.target.value;
    setDestSearchQuery(query);
    if (destSearchTimeoutRef.current) clearTimeout(destSearchTimeoutRef.current);
    destSearchTimeoutRef.current = setTimeout(() => {
      searchCustomers(query, false);
    }, CUSTOMER_SEARCH_DEBOUNCE_MS);
  };

  // Select a customer from dropdown
  const handleSelectSource = useCallback(async (customer) => {
    setSelectedSource(customer);
    setSourceSearchQuery('');
    setSourceSearchResults([]);
    setShowSourceDropdown(false);
    setLoadingSourceStats(true);
    const stats = await fetchStats(customer.id);
    setSourceStats(stats);
    setLoadingSourceStats(false);
    // Clear dest if same customer selected
    if (selectedDest && selectedDest.id === customer.id) {
      setSelectedDest(null);
      setDestStats(null);
    }
  }, [fetchStats, selectedDest]);

  const handleSelectDest = useCallback(async (customer) => {
    setSelectedDest(customer);
    setDestSearchQuery('');
    setDestSearchResults([]);
    setShowDestDropdown(false);
    setLoadingDestStats(true);
    const stats = await fetchStats(customer.id);
    setDestStats(stats);
    setLoadingDestStats(false);
    // Clear source if same customer selected
    if (selectedSource && selectedSource.id === customer.id) {
      setSelectedSource(null);
      setSourceStats(null);
    }
  }, [fetchStats, selectedSource]);

  const handleRemoveSource = () => {
    setSelectedSource(null);
    setSourceStats(null);
  };

  const handleRemoveDest = () => {
    setSelectedDest(null);
    setDestStats(null);
  };

  const canMerge = selectedSource && selectedDest && selectedSource.id !== selectedDest.id;

  const handleMerge = async () => {
    if (!canMerge) return;

    const confirmMessage = `Are you sure you want to merge '${selectedSource.first_name} ${selectedSource.last_name}' into '${selectedDest.first_name} ${selectedDest.last_name}'?\n\nAll records from '${selectedSource.first_name} ${selectedSource.last_name}' will be transferred to '${selectedDest.first_name} ${selectedDest.last_name}'.${deleteSource ? `\n\nThe source customer will be soft-deleted and can be restored by an administrator if needed.` : ''}${mergeRelationships ? '\n\nRelationships will be merged (duplicates skipped).' : '\n\nRelationships will be deleted.'}`;

    if (!window.confirm(confirmMessage)) return;

    setIsMerging(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/customers/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sourceCustomerId: selectedSource.id,
          destinationCustomerId: selectedDest.id,
          deleteSource: deleteSource,
          mergeRelationships: mergeRelationships
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Merge failed');
      }

      const data = await res.json();

      // Build success message
      const mergedItems = [];
      if (data.merged.transactions > 0) mergedItems.push(`${data.merged.transactions} transaction${data.merged.transactions !== 1 ? 's' : ''}`);
      if (data.merged.repairs > 0) mergedItems.push(`${data.merged.repairs} repair${data.merged.repairs !== 1 ? 's' : ''}`);
      if (data.merged.appraisals > 0) mergedItems.push(`${data.merged.appraisals} appraisal${data.merged.appraisals !== 1 ? 's' : ''}`);
      if (data.merged.storeCredits > 0) mergedItems.push(`${data.merged.storeCredits} store credit${data.merged.storeCredits !== 1 ? 's' : ''}`);
      if (data.merged.messages > 0) mergedItems.push(`${data.merged.messages} message${data.merged.messages !== 1 ? 's' : ''}`);
      if (data.merged.conversations > 0) mergedItems.push(`${data.merged.conversations} conversation${data.merged.conversations !== 1 ? 's' : ''}`);
      if (data.merged.relationshipsMerged > 0 || data.merged.relationshipsRelatedMerged > 0) {
        const totalRel = (data.merged.relationshipsMerged || 0) + (data.merged.relationshipsRelatedMerged || 0);
        mergedItems.push(`${totalRel} relationship${totalRel !== 1 ? 's' : ''}`);
      }
      if (data.merged.relationshipsDeleted > 0) mergedItems.push(`${data.merged.relationshipsDeleted} relationship${data.merged.relationshipsDeleted !== 1 ? 's' : ''} deleted`);

      const summary = mergedItems.length > 0
        ? `Successfully merged ${mergedItems.join(', ')} into '${selectedDest.first_name} ${selectedDest.last_name}'.`
        : `Successfully merged '${selectedSource.first_name} ${selectedSource.last_name}' into '${selectedDest.first_name} ${selectedDest.last_name}'.`;

      setSuccess(summary);

      // Reset form
      setSelectedSource(null);
      setSourceStats(null);
      setSelectedDest(null);
      setDestStats(null);

      // Notify parent after a short delay so user sees success message
      setTimeout(() => {
        onSuccess();
      }, 1500);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsMerging(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!showModal) return null;

  const formatPhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const renderSearchColumn = (isSource) => (
    <div className="flex flex-col h-full">
      {/* Search Input */}
      <div className="relative" ref={isSource ? sourceDropdownRef : destDropdownRef}>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search by name, phone, email..."
            autoComplete="off"
            value={isSource ? sourceSearchQuery : destSearchQuery}
            onChange={isSource ? handleSourceSearchChange : handleDestSearchChange}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
          {isSource && isSourceSearching && (
            <div className="flex items-center">
              <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {!isSource && isDestSearching && (
            <div className="flex items-center">
              <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {/* Dropdown Results */}
        {(isSource ? showSourceDropdown : showDestDropdown) && (
          (isSource ? sourceSearchResults.length : destSearchResults.length) > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
              {(isSource ? sourceSearchResults : destSearchResults).map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  onClick={() => isSource ? handleSelectSource(customer) : handleSelectDest(customer)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                >
                  <div className="font-medium">{customer.first_name} {customer.last_name}</div>
                  <div className="text-gray-500">
                    {customer.mobile_phone || customer.home_phone || customer.work_phone || 'No phone'}
                  </div>
                </button>
              ))}
            </div>
          )
        )}
      </div>

      {/* Selected Customer Card */}
      <div className="flex-1 mt-4">
        {isSource && selectedSource ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-semibold text-gray-900">
                  {selectedSource.first_name} {selectedSource.last_name}
                </h4>
                <p className="text-sm text-gray-500">{formatPhone(selectedSource.mobile_phone || selectedSource.home_phone || selectedSource.work_phone)}</p>
                {selectedSource.email && <p className="text-sm text-gray-500">{selectedSource.email}</p>}
              </div>
              <button
                type="button"
                onClick={handleRemoveSource}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ×
              </button>
            </div>

            {loadingSourceStats && (
              <div className="mt-3 flex items-center text-sm text-gray-500">
                <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mr-2" />
                Loading stats...
              </div>
            )}

            {sourceStats && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Transactions</div>
                  <div className="font-semibold">{sourceStats.transaction_count}</div>
                  <div className="text-xs text-gray-400">{formatCurrency(sourceStats.transaction_total)}</div>
                </div>
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Repairs</div>
                  <div className="font-semibold">{sourceStats.repair_count}</div>
                </div>
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Appraisals</div>
                  <div className="font-semibold">{sourceStats.appraisal_count}</div>
                </div>
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Messages</div>
                  <div className="font-semibold">{sourceStats.message_count}</div>
                </div>
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Conversations</div>
                  <div className="font-semibold">{sourceStats.conversation_count}</div>
                </div>
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Store Credits</div>
                  <div className="font-semibold">{sourceStats.store_credit_count}</div>
                </div>
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Relationships</div>
                  <div className="font-semibold">{sourceStats.relationship_count}</div>
                </div>
              </div>
            )}
          </div>
        ) : isSource && (
          <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center text-gray-400">
            <p className="text-sm">Search and select the customer to merge from</p>
          </div>
        )}

        {!isSource && selectedDest ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-semibold text-gray-900">
                  {selectedDest.first_name} {selectedDest.last_name}
                </h4>
                <p className="text-sm text-gray-500">{formatPhone(selectedDest.mobile_phone || selectedDest.home_phone || selectedDest.work_phone)}</p>
                {selectedDest.email && <p className="text-sm text-gray-500">{selectedDest.email}</p>}
              </div>
              <button
                type="button"
                onClick={handleRemoveDest}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ×
              </button>
            </div>

            {loadingDestStats && (
              <div className="mt-3 flex items-center text-sm text-gray-500">
                <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mr-2" />
                Loading stats...
              </div>
            )}

            {destStats && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Transactions</div>
                  <div className="font-semibold">{destStats.transaction_count}</div>
                  <div className="text-xs text-gray-400">{formatCurrency(destStats.transaction_total)}</div>
                </div>
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Repairs</div>
                  <div className="font-semibold">{destStats.repair_count}</div>
                </div>
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Appraisals</div>
                  <div className="font-semibold">{destStats.appraisal_count}</div>
                </div>
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Messages</div>
                  <div className="font-semibold">{destStats.message_count}</div>
                </div>
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Conversations</div>
                  <div className="font-semibold">{destStats.conversation_count}</div>
                </div>
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Store Credits</div>
                  <div className="font-semibold">{destStats.store_credit_count}</div>
                </div>
                <div className="bg-white rounded p-2 border">
                  <div className="text-gray-500">Relationships</div>
                  <div className="font-semibold">{destStats.relationship_count}</div>
                </div>
              </div>
            )}
          </div>
        ) : !isSource && (
          <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center text-gray-400">
            <p className="text-sm">Search and select the customer to merge into</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Merge Customers</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Warning Banner */}
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded mb-4 text-sm">
          <strong>Warning:</strong> All records from the source customer (left) will be transferred to the destination customer (right).{deleteSource ? ' The source customer will be soft-deleted.' : ''}
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-h-[300px]">
          {/* Source Column */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-3 h-3 bg-red-400 rounded-full"></span>
              <h4 className="font-medium text-gray-700">
                {deleteSource ? 'Source (Will be removed)' : 'Source (Records will be transferred)'}
              </h4>
            </div>
            {renderSearchColumn(true)}
          </div>

          {/* Arrow Divider */}
          <div className="hidden md:flex items-center justify-center absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="bg-white rounded-full p-2 border border-gray-200 shadow">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </div>
          </div>

          {/* Destination Column */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-3 h-3 bg-green-500 rounded-full"></span>
              <h4 className="font-medium text-gray-700">Destination (Will be kept)</h4>
            </div>
            {renderSearchColumn(false)}
          </div>
        </div>

        {/* Delete Source Checkbox */}
        {!success && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={deleteSource}
                onChange={() => setDeleteSource(!deleteSource)}
                className="rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
              />
              <span className="text-sm text-red-600 font-medium" onClick={() => setDeleteSource(!deleteSource)}>
                Soft-delete source customer after merge
              </span>
            </label>
            {!deleteSource && (
              <p className="text-xs text-gray-500 mt-1 ml-6">
                The source customer will remain active after records are transferred.
              </p>
            )}

            {/* Merge Relationships Checkbox */}
            <div className="mt-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mergeRelationships}
                  onChange={() => setMergeRelationships(!mergeRelationships)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
                <span className="text-sm text-gray-700 font-medium" onClick={() => setMergeRelationships(!mergeRelationships)}>
                  Merge relationships (uncheck to delete)
                </span>
              </label>
              {!mergeRelationships && (
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  All relationships involving the source customer will be deleted.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleMerge}
            disabled={!canMerge || isMerging}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isMerging ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Merging...
              </span>
            ) : (
              'Merge Customers'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
